import { createHttpClient } from '@adapters/HttpClient';
import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { apiClientWH } from '@shared/services';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { FiltersApiAdapter } from '../adapters/FiltersApiAdapter';

interface OrdersShippedFiltersState {
  processedAt: DateRange | undefined;
  createdAt: DateRange | undefined;
  shop: string[];
  paymentMethod: string[];
  products: string[];
  courier: string[];
  trackingNumber: string;
}

interface UseOrdersShippedFiltersProps {
  initialConditions?: FilterConditionOutput[];
  onChange?: (conditions: FilterConditionOutput[]) => void;
}

function formatDateForAPI(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export const useOrdersShippedFilters = ({ initialConditions = [], onChange }: UseOrdersShippedFiltersProps) => {
  const [filters, setFilters] = useState<OrdersShippedFiltersState>(() => parseConditionsToState(initialConditions));

  const httpClient = createHttpClient(apiClientWH);
  const filtersAdapter = useMemo(() => new FiltersApiAdapter(httpClient), [httpClient]);

  const { data: shops = [], isLoading: isLoadingShops } = useQuery({
    queryKey: ['orders-shipped-filters-shops'],
    queryFn: () => filtersAdapter.getShops(),
  });

  const { data: paymentMethods = [], isLoading: isLoadingPaymentMethods } = useQuery({
    queryKey: ['orders-shipped-filters-payment-methods'],
    queryFn: () => filtersAdapter.getPaymentMethods(),
  });

  const { data: products = [], isLoading: isLoadingProducts } = useQuery({
    queryKey: ['orders-shipped-filters-products'],
    queryFn: () => filtersAdapter.getProducts(),
  });

  const { data: couriers = [], isLoading: isLoadingCouriers } = useQuery({
    queryKey: ['orders-shipped-filters-couriers'],
    queryFn: () => filtersAdapter.getCouriers(),
  });

  const shopOptions = useMemo(() => shops.map((s) => ({ value: s.id, label: s.name })), [shops]);
  const paymentMethodOptions = useMemo(
    () =>
      paymentMethods.map((m) => ({
        value: m.id,
        label: m.name,
      })),
    [paymentMethods],
  );
  const productOptions = useMemo(
    () =>
      products.map((p) => ({
        value: p.id,
        label: p.name,
      })),
    [products],
  );
  const courierOptions = useMemo(
    () =>
      couriers.map((c) => ({
        value: c.id,
        label: c.name,
      })),
    [couriers],
  );

  const convertStateToConditions = useCallback((state: OrdersShippedFiltersState): FilterConditionOutput[] => {
    const conditions: FilterConditionOutput[] = [];

    if (state.processedAt?.from && state.processedAt?.to) {
      conditions.push({
        column: 'processed_at',
        action: 'greater_equal',
        value: formatDateForAPI(state.processedAt.from),
      });
      conditions.push({
        column: 'processed_at',
        action: 'less_equal',
        value: formatDateForAPI(state.processedAt.to),
      });
    }

    if (state.createdAt?.from && state.createdAt?.to) {
      conditions.push({
        column: 'order_created_at',
        action: 'greater_equal',
        value: formatDateForAPI(state.createdAt.from),
      });
      conditions.push({
        column: 'order_created_at',
        action: 'less_equal',
        value: formatDateForAPI(state.createdAt.to),
      });
    }

    if (state.shop.length > 0) {
      conditions.push({
        column: 'shop_id',
        action: 'in',
        value: state.shop,
      });
    }

    if (state.paymentMethod.length > 0) {
      conditions.push({
        column: 'payment_method_id',
        action: 'in',
        value: state.paymentMethod,
      });
    }

    if (state.products.length > 0) {
      conditions.push({
        column: 'product_id',
        action: 'in',
        value: state.products,
      });
    }

    if (state.courier.length > 0) {
      conditions.push({
        column: 'courier_id',
        action: 'in',
        value: state.courier,
      });
    }

    if (state.trackingNumber?.trim()) {
      conditions.push({
        column: 'tracking_number',
        action: 'contains',
        value: state.trackingNumber.trim(),
      });
    }

    return conditions;
  }, []);

  const updateFilters = useCallback(
    (updates: Partial<OrdersShippedFiltersState>) => {
      setFilters((prev) => {
        const newState = { ...prev, ...updates };
        onChange?.(convertStateToConditions(newState));
        return newState;
      });
    },
    [convertStateToConditions, onChange],
  );

  const handleProcessedAtChange = useCallback(
    (range: DateRange | undefined) => updateFilters({ processedAt: range }),
    [updateFilters],
  );
  const handleCreatedAtChange = useCallback(
    (range: DateRange | undefined) => updateFilters({ createdAt: range }),
    [updateFilters],
  );
  const handleShopChange = useCallback((values: string[]) => updateFilters({ shop: values }), [updateFilters]);
  const handlePaymentMethodChange = useCallback(
    (values: string[]) => updateFilters({ paymentMethod: values }),
    [updateFilters],
  );
  const handleProductsChange = useCallback((values: string[]) => updateFilters({ products: values }), [updateFilters]);
  const handleCourierChange = useCallback((values: string[]) => updateFilters({ courier: values }), [updateFilters]);
  const handleTrackingNumberChange = useCallback(
    (value: string) => updateFilters({ trackingNumber: value }),
    [updateFilters],
  );

  const clearAllFilters = useCallback(() => {
    const emptyState: OrdersShippedFiltersState = {
      processedAt: undefined,
      createdAt: undefined,
      shop: [],
      paymentMethod: [],
      products: [],
      courier: [],
      trackingNumber: '',
    };
    setFilters(emptyState);
    onChange?.([]);
  }, [onChange]);

  useEffect(() => {
    setFilters(parseConditionsToState(initialConditions));
  }, [initialConditions]);

  return {
    filters,
    handleProcessedAtChange,
    handleCreatedAtChange,
    handleShopChange,
    handlePaymentMethodChange,
    handleProductsChange,
    handleCourierChange,
    handleTrackingNumberChange,
    clearAllFilters,
    shopOptions,
    paymentMethodOptions,
    productOptions,
    courierOptions,
    isLoadingShops,
    isLoadingPaymentMethods,
    isLoadingProducts,
    isLoadingCouriers,
  };
};

function parseConditionsToState(conditions: FilterConditionOutput[]): OrdersShippedFiltersState {
  const state: OrdersShippedFiltersState = {
    processedAt: undefined,
    createdAt: undefined,
    shop: [],
    paymentMethod: [],
    products: [],
    courier: [],
    trackingNumber: '',
  };

  const byColumn: Record<string, FilterConditionOutput[]> = {};
  conditions.forEach((c) => {
    if (!byColumn[c.column]) byColumn[c.column] = [];
    byColumn[c.column].push(c);
  });

  const parseDateRange = (column: string): DateRange | undefined => {
    const arr = byColumn[column] || [];
    const from = arr.find((c) => c.action === 'greater_equal');
    const to = arr.find((c) => c.action === 'less_equal');
    if (from?.value && to?.value) {
      return {
        from: new Date(from.value as string),
        to: new Date(to.value as string),
      };
    }
    return undefined;
  };

  state.processedAt = parseDateRange('processed_at');
  state.createdAt = parseDateRange('order_created_at');

  const getArray = (column: string): string[] => {
    const c = byColumn[column]?.find((x) => x.action === 'in');
    if (!c?.value) return [];
    return Array.isArray(c.value) ? c.value : [String(c.value)];
  };

  state.shop = getArray('shop_id');
  state.paymentMethod = getArray('payment_method_id');
  state.products = getArray('product_id');
  state.courier = getArray('courier_id');

  const trackingCond = byColumn['tracking_number']?.find((c) => c.action === 'contains');
  if (trackingCond?.value) state.trackingNumber = trackingCond.value as string;

  return state;
}
